import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Users, GraduationCap, Building2, BookOpen, ClipboardCheck, TrendingUp, ShieldCheck } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

function StatCard({ label, value, tone = "indigo", icon: Icon, testid }) {
  const tones = {
    indigo: "bg-indigo-50 text-indigo-700",
    amber: "bg-amber-50 text-amber-700",
    emerald: "bg-emerald-50 text-emerald-700",
    rose: "bg-rose-50 text-rose-700",
    sky: "bg-sky-50 text-sky-700",
    violet: "bg-violet-50 text-violet-700",
  };
  return (
    <div className="qs-card p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">{label}</p>
          <p className="font-display text-3xl font-black text-slate-900 mt-2">{value}</p>
        </div>
        <div className={`w-10 h-10 rounded-xl grid place-items-center ${tones[tone]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
  }, []);

  const roleData = stats
    ? [
        { role: "Admins", count: stats.admins },
        { role: "Teachers", count: stats.teachers },
        { role: "Students", count: stats.students },
      ]
    : [];

  return (
    <AppShell title="Platform overview" subtitle="System-wide activity across Prismiq.">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-stats">
        <StatCard label="Total Users" value={stats?.users_total ?? 0} icon={Users} tone="indigo" testid="admin-stat-users" />
        <StatCard label="Institutions" value={stats?.institutions ?? 0} icon={Building2} tone="amber" testid="admin-stat-inst" />
        <StatCard label="Exams" value={stats?.exams ?? 0} icon={BookOpen} tone="sky" testid="admin-stat-exams" />
        <StatCard label="Completed Attempts" value={stats?.attempts_completed ?? 0} icon={ClipboardCheck} tone="emerald" testid="admin-stat-attempts" />
        <StatCard label="Admins" value={stats?.admins ?? 0} icon={ShieldCheck} tone="violet" testid="admin-stat-admins" />
        <StatCard label="Teachers" value={stats?.teachers ?? 0} icon={GraduationCap} tone="amber" testid="admin-stat-teachers" />
        <StatCard label="Students" value={stats?.students ?? 0} icon={Users} tone="emerald" testid="admin-stat-students" />
        <StatCard label="Avg Score" value={`${stats?.average_score ?? 0}%`} icon={TrendingUp} tone="rose" testid="admin-stat-avg" />
      </div>

      <div className="mt-6 qs-card p-6">
        <h3 className="font-display text-lg font-bold text-slate-900">Users by role</h3>
        <div className="h-72 mt-4">
          <ResponsiveContainer>
            <BarChart data={roleData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="role" stroke="#94a3b8" fontSize={12} />
              <YAxis stroke="#94a3b8" fontSize={12} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
              <Bar dataKey="count" fill="#4f46e5" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </AppShell>
  );
}
